import axios, { AxiosError, AxiosRequestConfig, InternalAxiosRequestConfig } from "axios";

export interface AuthTokenProvider {
  getAccessToken: () => string | null;
  onRefreshSuccess: (newAccessToken: string) => void;
  onUnauthenticated: () => void;
  refresh: () => Promise<string>;
}

interface AuthRetryConfig extends InternalAxiosRequestConfig {
  _retried?: boolean;
  _authHeaderSet?: boolean;
}

let tokenProvider: AuthTokenProvider | null = null;

export function installAuthTokenProvider(provider: AuthTokenProvider): void {
  tokenProvider = provider;
}

export const api = axios.create({
  baseURL: "/api",
  withCredentials: true,
});

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const cfg = config as AuthRetryConfig;
  if (cfg._authHeaderSet) {
    return cfg;
  }
  const token = tokenProvider?.getAccessToken();
  if (token) {
    cfg.headers.set("Authorization", `Bearer ${token}`);
  }
  return cfg;
});

let refreshInFlight: Promise<string> | null = null;

api.interceptors.response.use(
  (r) => r,
  async (error: AxiosError) => {
    const originalReq = error.config as AuthRetryConfig | undefined;
    if (!originalReq || !error.response) throw error;
    if (error.response.status !== 401) throw error;
    if (originalReq._retried) throw error;
    const url = originalReq.url ?? "";
    if (url.startsWith("/auth/login") || url.startsWith("/auth/refresh")) {
      throw error;
    }
    if (!tokenProvider) throw error;

    try {
      if (!refreshInFlight) {
        refreshInFlight = tokenProvider.refresh().finally(() => {
          refreshInFlight = null;
        });
      }
      const newToken = await refreshInFlight;
      originalReq._retried = true;
      originalReq._authHeaderSet = true;
      originalReq.headers.set("Authorization", `Bearer ${newToken}`);
      return api.request(originalReq as AxiosRequestConfig);
    } catch (refreshErr) {
      tokenProvider.onUnauthenticated();
      throw refreshErr;
    }
  },
);

/**
 * Конфиг axios с отслеживанием отдачи байтов.
 *
 * ⚠️ Один адаптер на все загрузки файлов. Их уже четыре (фото приёмки, фото
 * паллеты, фото сессии, и дальше будут), и в каждой лежала своя копия одного и
 * того же `if (e.total)`. Копия — это способ забыть: два из четырёх загрузчиков
 * прогресса так и не получили, и на слабом вайфае там снова «непонятно, идёт ли
 * отправка».
 *
 * `total` браузер сообщает не всегда (нет `Content-Length` у потока) — тогда
 * процент НЕ выдумываем, вызывающий покажет спиннер. Ложная полоса, которая
 * доезжает до 90 % и замирает, хуже честного «идёт отправка».
 */
export function uploadProgress(
  onProgress?: (percent: number) => void,
): { onUploadProgress?: (e: { loaded: number; total?: number }) => void } {
  if (!onProgress) return {};
  return {
    onUploadProgress: (e) => {
      if (e.total) onProgress((e.loaded / e.total) * 100);
    },
  };
}
